// src/AuthContext/Authcontext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API, { clearPendingRequests } from "../api/config";
import { dataService } from "../services/dataService";
import { cacheService } from "../services/cacheService";

export const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [userPermission, setUserPermission] = useState({});
  const [languageLoaded, setLanguageLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const navigate = useNavigate();
  const initialCheckDone = useRef(false);

  const logout = useCallback(async (manualLogout = false) => {
    // Clear pending requests
    clearPendingRequests();
    
    // Clear cache on logout
    cacheService.clear();
    
    if (manualLogout) {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          await API.post("logout", {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (error) {
        console.error("Logout error:", error);
      }
    }

    localStorage.clear();
    sessionStorage.clear();

    setIsAuthenticated(false);
    setUser(null);
    setTokenExpiry(null);
    setLanguageLoaded(false);
    setUserPermission({});

    navigate("/", { replace: true });
  }, [navigate]);

  // Check token expiry periodically
  useEffect(() => {
    const checkTokenExpiry = () => {
      if (tokenExpiry && new Date() > tokenExpiry) {
        logout();
      }
    };
    
    const interval = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(interval);
  }, [logout, tokenExpiry]);

  // Check session on initial load - optimized
  const checkSession = useCallback(() => {
    try {
      const storedUser = localStorage.getItem("user");
      const storedExpiry = localStorage.getItem("expires_in");

      if (storedUser && storedExpiry) {
        const expiryDate = new Date(storedExpiry);
        if (new Date() < expiryDate) {
          const userData = JSON.parse(storedUser);
          setIsAuthenticated(true);
          setUser(userData);
          setTokenExpiry(expiryDate);
          
          // Initialize DataService
          dataService.initialize(userData);
          
          // Restore language
          if (userData.language_preferences) {
            localStorage.setItem('user_language', userData.language_preferences);
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('languageChanged', { 
                detail: { language: userData.language_preferences } 
              }));
            }, 50);
          }
          return true;
        } else {
          logout();
        }
      }
    } catch (error) {
      console.error('Session check error:', error);
    }
    return false;
  }, [logout]);

  // Initial load - only once
  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;
    
    const hasSession = checkSession();
    setIsLoading(false);
    
    // Listen for session updates
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'expires_in') {
        checkSession();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkSession]);

  // Listen for user object updates from LanguageContext
  useEffect(() => {
    const handleAuthUserUpdate = (event) => {
      const updatedUser = event.detail?.user;
      if (updatedUser) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        dataService.initialize(updatedUser);
      }
    };

    window.addEventListener('authUserUpdate', handleAuthUserUpdate);
    return () => window.removeEventListener('authUserUpdate', handleAuthUserUpdate);
  }, []);

  // Fetch permissions - optimized with caching and condition
  useEffect(() => {
    // Only fetch permissions for non-admin users (role_id !== 1 and role_id !== 4)
    if (!user?.userid || user?.role_id === 1 || user?.role_id === 4) {
      return;
    }

    const fetchPermissions = async () => {
      try {
        const response = await API.get(`getPermissionPage?userid=${user.userid}`, {
          headers: { Authorization: `Bearer ${user.access_token}` },
        });

        let permissionData = response.data?.permisiondata_info || response.data?.msg || [];

        if (!Array.isArray(permissionData)) {
          permissionData = [];
        }

        const permissionsMap = {};

        permissionData.forEach((perm) => {
          permissionsMap[perm.Pagename] = {
            view: perm.permissioncat_id?.includes("1"),
            add: perm.permissioncat_id?.includes("2"),
            update: perm.permissioncat_id?.includes("3"),
            delete: perm.permissioncat_id?.includes("4"),
            download: perm.permissioncat_id?.includes("5"),
            pageId: perm.pageid,
          };
        });

        setUserPermission(permissionsMap);
      } catch (error) {
        if (!error.isCanceled) {
          console.error("Error fetching permissions:", error);
        }
        setUserPermission({});
      }
    };

    fetchPermissions();
  }, [user?.userid, user?.role_id, user?.access_token]);

  // Login function
  const login = useCallback(async (userData) => {
    const expiryDate = new Date(userData.expires_in);

    setIsAuthenticated(true);
    setUser(userData);
    setTokenExpiry(expiryDate);

    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("access_token", userData.access_token);
    localStorage.setItem("token", userData.access_token);
    localStorage.setItem("expires_in", userData.expires_in);

    // Initialize DataService
    dataService.initialize(userData);

    // Set language
    const userLanguage = userData.language_preferences || 'en';
    localStorage.setItem('user_language', userLanguage);
    
    // Dispatch language event
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('languageChanged', { 
        detail: { language: userLanguage } 
      }));
    }, 50);
    
    setLanguageLoaded(true);

    // Navigate based on role
    const destination = userData.role_id === 4 ? "/customerdashboard" : "/dashboard";
    navigate(destination);
  }, [navigate]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      login, 
      logout, 
      tokenExpiry, 
      userPermission,
      languageLoaded,
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);